import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Save, FileEdit, Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8B7500";

// Business 2.0-indhold. Bump version, hvis strukturen ændres — så ignoreres gammelt gemt indhold.
const DEFAULT_CONTENT = {
  version: 2,
  promo: "Vi overtager bogføring, løn, fakturering og bilernes administration, du passer vejen.",
  hero: {
    badge: "Votra Business · Vognmandens driftskontor",
    titleStart: "Vi driver forretningen",
    titleAccent: "bag din vognmandsforretning",
    subtitle:
      "Votra Business er din eksterne drifts- og økonomiafdeling. Vi bogfører, fakturerer, følger dine debitorer, kører lønnen, holder styr på bilernes omkostninger og chaufførernes dokumenter, og du får overblikket i din egen portal med en fast rådgiver på den anden ende.",
    primaryCta: "Få en driftsgennemgang",
    secondaryCta: "Se hvad vi overtager",
  },
  finalCta: {
    title: "Du skal aldrig selv samle din forretning fra fem forskellige leverandører.",
    button: "Få en driftsgennemgang",
  },
  form: {
    sectionLabel: "Kontakt",
    sectionTitle: "Få en driftsgennemgang",
    sectionSubtitle:
      "Vi gennemgår din forretning og lægger et konkret oplæg frem: hvad vi overtager, hvad det kræver af dig, og hvad det koster. Uden forpligtelse.",
    submitButton: "Book driftsgennemgang",
    helperText: "Vi vender tilbage inden for en hverdag. Ingen forpligtelser.",
    successTitle: "Tak for din henvendelse!",
    successText: "Vi vender tilbage inden for en hverdag på telefon eller mail.",
    labels: {
      name: "Navn",
      company: "Virksomhed",
      phone: "Telefon",
      email: "Email",
      trucks: "Antal biler",
      message: "Hvad sidder du selv med i dag?",
    },
  },
  footer: { tagline: "Vognmandens driftskontor" },
  testimonials: {
    sectionLabel: "Kundeudtalelser",
    sectionTitle: "Det siger vognmændene",
    sectionSubtitle:
      "Vi offentliggør kun udtalelser, vi har modtaget fra en navngiven kunde, der har godkendt teksten. Er der ingen udtalelser, vises sektionen ikke.",
    items: [],
  },
};

const getAuthHeader = () => {
  const token = localStorage.getItem("minskoleToken") || localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Deep merge defaults med overrides
const merge = (def, over) => {
  if (!over) return def;
  if (Array.isArray(def)) return Array.isArray(over) ? over : def;
  if (typeof def === "object" && def !== null) {
    const out = { ...def };
    Object.keys(over).forEach((k) => {
      out[k] = merge(def[k], over[k]);
    });
    return out;
  }
  return over !== undefined ? over : def;
};

const Field = ({ label, value, onChange, multiline, placeholder }) => (
  <label className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
    {multiline ? (
      <textarea
        rows={3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
      />
    ) : (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
      />
    )}
  </label>
);

const SectionCard = ({ title, children, hint }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
    <h3 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
      <span className="w-1 h-5 rounded-full" style={{ backgroundColor: GOLD }} />
      {title}
    </h3>
    {hint && <p className="text-xs text-slate-500 mb-4 ml-3">{hint}</p>}
    <div className="space-y-4 mt-4">{children}</div>
  </div>
);

const BusinessContentEditor = () => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/business/content`);
      // Gammelt Business 1.0-indhold ignoreres, så den nye side ikke får de gamle tekster tilbage.
      setContent(res.data?.version === DEFAULT_CONTENT.version ? merge(DEFAULT_CONTENT, res.data) : DEFAULT_CONTENT);
    } catch {
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/business/content`, { ...content, version: DEFAULT_CONTENT.version }, { headers: getAuthHeader() });
      toast.success("Gemt, ændringerne er live nu");
    } catch (e) {
      toast.error("Kunne ikke gemme: " + (e.response?.data?.detail || ""));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (window.confirm("Nulstil til standard-indhold? Dine ændringer mistes.")) {
      setContent(DEFAULT_CONTENT);
      toast.info("Indhold nulstillet (klik Gem for at gemme)");
    }
  };

  const update = (path, value) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let cur = next;
      const keys = path.slice(0, -1);
      const last = path[path.length - 1];
      keys.forEach((k) => { cur = cur[k]; });
      cur[last] = value;
      return next;
    });
  };

  const addTestimonial = () => {
    setContent((p) => {
      const next = JSON.parse(JSON.stringify(p));
      if (!next.testimonials) next.testimonials = { ...DEFAULT_CONTENT.testimonials };
      if (!next.testimonials.items) next.testimonials.items = [];
      next.testimonials.items.push({ quote: "", name: "", role: "Vognmand · By" });
      return next;
    });
  };

  const removeTestimonial = (idx) => {
    setContent((p) => {
      const next = JSON.parse(JSON.stringify(p));
      next.testimonials.items.splice(idx, 1);
      return next;
    });
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Indlæser indhold...</div>;
  }

  return (
    <div data-testid="business-content-editor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileEdit className="h-6 w-6" style={{ color: GOLD_DARK }} /> Rediger Business-siden
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Hero, promo-stribe, CTA, kontaktformular og kundeudtalelser på votra.dk/business. Ændringer er live umiddelbart efter Gem.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm" data-testid="biz-content-reset-btn">
            Nulstil
          </button>
          <button onClick={save} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: GOLD_DARK }} data-testid="biz-content-save-btn">
            <Save className="h-4 w-4" /> {saving ? "Gemmer..." : "Gem ændringer"}
          </button>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6 text-sm text-amber-900">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <p>
          Ydelser, pakker, portal-funktioner, partnerroller og afgrænsninger redigeres ikke her. De er faktuelle oplysninger og
          vedligeholdes i koden (<code>src/data/business/</code>), så siden ikke kan komme til at love noget, vi ikke leverer.
        </p>
      </div>

      <SectionCard title="Promo-stribe (guld, øverst)">
        <Field label="Tekst" multiline value={content.promo} onChange={(v) => update(["promo"], v)} />
      </SectionCard>

      <SectionCard title="Hero-sektion (øverst)">
        <Field label="Badge / Eyebrow" value={content.hero.badge} onChange={(v) => update(["hero", "badge"], v)} />
        <Field label="Titel (hvid del)" value={content.hero.titleStart} onChange={(v) => update(["hero", "titleStart"], v)} />
        <Field label="Titel (guld accent, kursiv)" value={content.hero.titleAccent} onChange={(v) => update(["hero", "titleAccent"], v)} />
        <Field label="Underoverskrift" multiline value={content.hero.subtitle} onChange={(v) => update(["hero", "subtitle"], v)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Primær knap" value={content.hero.primaryCta} onChange={(v) => update(["hero", "primaryCta"], v)} />
          <Field label="Sekundær knap" value={content.hero.secondaryCta} onChange={(v) => update(["hero", "secondaryCta"], v)} />
        </div>
      </SectionCard>

      <SectionCard title="Afsluttende CTA">
        <Field label="Titel" multiline value={content.finalCta.title} onChange={(v) => update(["finalCta", "title"], v)} />
        <Field label="Knap" value={content.finalCta.button} onChange={(v) => update(["finalCta", "button"], v)} />
      </SectionCard>

      <SectionCard title="Kontaktformular">
        <Field label="Section label" value={content.form.sectionLabel} onChange={(v) => update(["form", "sectionLabel"], v)} />
        <Field label="Section titel" value={content.form.sectionTitle} onChange={(v) => update(["form", "sectionTitle"], v)} />
        <Field label="Section underoverskrift" multiline value={content.form.sectionSubtitle} onChange={(v) => update(["form", "sectionSubtitle"], v)} />
        <Field label="Submit-knap" value={content.form.submitButton} onChange={(v) => update(["form", "submitButton"], v)} />
        <Field label="Hjælpe-tekst (under knappen)" value={content.form.helperText} onChange={(v) => update(["form", "helperText"], v)} />
        <Field label="Success titel" value={content.form.successTitle} onChange={(v) => update(["form", "successTitle"], v)} />
        <Field label="Success besked" multiline value={content.form.successText} onChange={(v) => update(["form", "successText"], v)} />
      </SectionCard>

      <SectionCard title="Footer">
        <Field label="Tagline (under logo)" value={content.footer.tagline} onChange={(v) => update(["footer", "tagline"], v)} />
      </SectionCard>

      <SectionCard
        title="Kundeudtalelser"
        hint="Sektionen vises kun på forsiden, når der ligger mindst én udtalelse her. Indtast kun udtalelser, du har modtaget fra kunden, med navn og godkendelse.">
        <Field label="Section label" value={content.testimonials?.sectionLabel} onChange={(v) => update(["testimonials", "sectionLabel"], v)} />
        <Field label="Section titel" value={content.testimonials?.sectionTitle} onChange={(v) => update(["testimonials", "sectionTitle"], v)} />
        <Field label="Section underoverskrift" multiline value={content.testimonials?.sectionSubtitle} onChange={(v) => update(["testimonials", "sectionSubtitle"], v)} />
        <div className="mt-4 space-y-4">
          {(content.testimonials?.items || []).map((it, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-600">Udtalelse {idx + 1}</span>
                <button onClick={() => removeTestimonial(idx)} className="px-2 text-red-500 hover:bg-red-50 rounded" data-testid={`biz-remove-testimonial-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <Field label="Citat" multiline value={it.quote} onChange={(v) => update(["testimonials", "items", idx, "quote"], v)} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <Field label="Navn" value={it.name} onChange={(v) => update(["testimonials", "items", idx, "name"], v)} />
                <Field label="Rolle / by" value={it.role} onChange={(v) => update(["testimonials", "items", idx, "role"], v)} />
              </div>
            </div>
          ))}
          <button onClick={addTestimonial} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-800" data-testid="biz-add-testimonial">
            <Plus className="h-4 w-4" /> Tilføj udtalelse
          </button>
        </div>
      </SectionCard>

      <div className="sticky bottom-4 flex justify-end mt-8">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white shadow-lg disabled:opacity-60"
          style={{ backgroundColor: GOLD_DARK }}>
          <Save className="h-4 w-4" /> {saving ? "Gemmer..." : "Gem ændringer"}
        </button>
      </div>
    </div>
  );
};

export default BusinessContentEditor;
export { DEFAULT_CONTENT, merge as mergeContent };
